import { Component, EventEmitter, Input, Output, ViewEncapsulation } from '@angular/core';
import Swal from 'sweetalert2/dist/sweetalert2.js';
import { FsCsvExportService, FsCsvFormat, FsCsvGroup, FsCsvMember } from '../fs-csv-export/fs-csv-export.service';

export interface FsReportMember {
  id: string;
  firstName: string;
  lastName: string;
  /** The group this member sits in — drives the CSV's Group column / grouped blocks. */
  groupName?: string;
  profileTypeId?: number;
}

export type FsReportKind = 'group_profile' | 'thinking_profiles' | 'csv_flat' | 'csv_grouped';

export interface FsReportTarget {
  /** What the download is OF: 'Group 1' | 'Tuesday Workshop' | '3 selected'. */
  title: string;
  members: FsReportMember[];
  /**
   * Whether the profile PDFs can actually run. FALSE everywhere today.
   *
   * The PDFs need a real backend: their Lambda payloads are built from live team objects
   * (~40 keys for Group Profile; nested team_profiles/table_member/team_lead plus a
   * team_advice fetch for Team Member Report). Our members are mock ids in localStorage.
   * Turning this on is a piece of work — a payload-builder per host — not a flag flip.
   */
  live: boolean;
  /**
   * Whether the CSV options should be offered. TRUE for every target that has a group concept —
   * which is all of them today.
   *
   * This used to be `false` for single groups and for participant selections, on the theory that a
   * partial CSV "misrepresents" the groups. That was wrong twice over: every member carries its own
   * `groupName`, so the flat Group column and the grouped blocks are accurate for ANY subset; and a
   * subset is precisely what someone who ticked four boxes asked for. Worse, the modal renders the
   * same "Download reports" button either way, so the flag silently produced a dialog with nothing
   * to download — reported as "I cannot download the CSV".
   *
   * Set `false` only for a future host whose members have no group at all — grouped output would
   * then be one nameless block. No current caller is in that position.
   */
  allowCsv: boolean;
}

/**
 * The download modal for a set of people. Shared: sessions use it today, the roster can adopt it
 * once someone builds the payload-builder (see the spec's Downloads section).
 *
 * Takes DATA, not ids to fetch — that's what lets a localStorage host and an API host both feed it.
 */
@Component({
  selector: 'app-fs-report-download',
  templateUrl: './fs-report-download.component.html',
  styleUrls: ['./fs-report-download.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class FsReportDownloadComponent {
  @Input() target!: FsReportTarget;
  @Output() closed = new EventEmitter<void>();

  constructor(private csv: FsCsvExportService) {}

  get count(): number { return this.target?.members?.length ?? 0; }

  download(kind: FsReportKind): void {
    if (kind === 'csv_flat' || kind === 'csv_grouped') {
      this.downloadCsv(kind === 'csv_grouped' ? 'grouped' : 'flat');
      this.closed.emit();
      return;
    }
    if (!this.target.live) {
      Swal.fire({
        title: 'Not available yet',
        text: 'Profile downloads will run once sessions are connected to live data.',
        icon: 'info',
        confirmButtonText: 'Got it',
      });
      return;
    }
    // Intentionally unreachable today. Wiring this needs a per-host payload builder — the
    // roster's Lambda payloads are not derivable from FsReportMember. See the spec.
    throw new Error('fs-report-download: live profile downloads are not implemented');
  }

  /**
   * Adapts the flat member list into groups for the shared exporter. `Map` preserves insertion
   * order, and every caller builds `target.members` by walking groups in board order, so
   * first-appearance order is board order.
   *
   * Note: bucketing through this `Map` means FLAT rows now come out grouped (all of one group's
   * members together) rather than in `target.members`' original order. Every committed caller
   * already builds that list by walking groups, so today's lists are group-contiguous and this
   * is invisible — but a host that feeds an interleaved, participant-scoped selection (e.g. A,
   * B, A) would see flat output reordered to A, A, B instead of the source order.
   */
  private downloadCsv(format: FsCsvFormat): void {
    const byName = new Map<string, FsCsvMember[]>();
    this.target.members.forEach(m => {
      const key = m.groupName ?? '';
      if (!byName.has(key)) { byName.set(key, []); }
      byName.get(key)!.push({ firstName: m.firstName, lastName: m.lastName });
    });
    const groups: FsCsvGroup[] = [...byName].map(([name, members]) => ({ name, members }));

    const slug = (this.target.title || 'session').toLowerCase().replace(/[^a-z0-9]+/g, '_');
    this.csv.download(groups, { format, groupColumnHeader: 'Group', filename: slug });
  }
}
